import React from 'react';
import { useTranslation } from 'react-i18next';
import { Paperclip, TriangleAlert, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { formatFileSize } from '@utils/dateUtils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TooltipIconButton } from '@/components/ui/tooltip-icon-button';
import type { UploadFile } from '@hooks/useDocumentUpload';

interface UploadFileListProps {
  files: UploadFile[];
  onRemove: (id: string) => void;
}

const UploadFileList: React.FC<UploadFileListProps> = ({ files, onRemove }) => {
  const { t } = useTranslation();

  if (files.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium">
          {t('upload.selectedFiles')} <span className="revy-figure">({files.length})</span>
        </CardTitle>
      </CardHeader>

      <CardContent className="pt-0">
        <ul className="divide-y">
          {files.map((entry) => {
            const isDuplicate = entry.status === 'duplicate';

            return (
              <li key={entry.id} className="flex items-center gap-3 py-2">
                <span className={isDuplicate ? 'text-warning' : 'text-muted-foreground'}>
                  {isDuplicate ? (
                    <TriangleAlert className="size-4" strokeWidth={1.9} />
                  ) : (
                    <Paperclip className="size-4" strokeWidth={1.9} />
                  )}
                </span>

                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm">{entry.file.name}</p>
                  <p className="text-xs revy-figure text-muted-foreground">
                    {formatFileSize(entry.file.size)}
                  </p>
                </div>

                {isDuplicate && (
                  <Badge variant="outline" className="shrink-0 border-warning/40 text-warning">
                    {t('upload.duplicate')}
                  </Badge>
                )}

                <TooltipIconButton
                  label={t('common.remove')}
                  icon={<X className="size-4" strokeWidth={2} />}
                  onClick={() => onRemove(entry.id)}
                  className="shrink-0"
                />
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
};

export default UploadFileList;
